import React, { useEffect, useState } from 'react';
import { Box, Skeleton, Text } from '@chakra-ui/react';
import Select from 'react-select';
import { api } from '../../services/api';
import { EmptyState, ErrorState } from '../states';
import { useActionToast } from '../../services/actionToast';

interface Institution {
  id: number;
  name: string;
}

interface InstitutionSelectorProps {
  // eslint-disable-next-line no-unused-vars
  onInstitutionChange?: (id: number | null) => void;
  selectedInstitutionId?: number | null;
}

export const InstitutionSelector: React.FC<InstitutionSelectorProps> = ({
  onInstitutionChange,
  selectedInstitutionId,
}) => {
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedValue, setSelectedValue] = useState<any>(null);
  const [loadError, setLoadError] = useState(false);
  const actionToast = useActionToast();

  useEffect(() => {
    fetchInstitutions();
  }, []);

  useEffect(() => {
    if (selectedInstitutionId && institutions.length > 0) {
      const institution = institutions.find((inst) => inst.id === selectedInstitutionId);
      if (institution) {
        setSelectedValue({
          value: institution.id,
          label: institution.name,
        });
      }
    }
  }, [selectedInstitutionId, institutions]);

  const fetchInstitutions = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await api.get('/v1/client/institutions');
      if (response.status === 200 && response.data.data) {
        setInstitutions(response.data.data);
      }
    } catch (error) {
      setLoadError(true);
      actionToast({
        title: 'Erro ao carregar instituições',
        description: 'Não foi possível carregar a lista de instituições.',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const options = institutions.map((institution) => ({
    value: institution.id,
    label: institution.name,
  }));

  const customStyles = {
    menu: (provided: any) => ({
      ...provided,
      zIndex: 9999,
      width: '100%',
      maxWidth: '350px',
    }),
    control: (styles: any, { isDisabled }: any) => ({
      ...styles,
      boxShadow: '0px 2px 4px #00000050',
      opacity: isDisabled ? 0.7 : 1,
      backgroundColor: '#FAFAFA',
      height: '2.5em',
      borderColor: 'inherit',
      width: '100%',
      maxWidth: '350px',
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      borderBottom: '1px solid #E2E8F0',
      borderRadius: '5px',
      padding: '10px 24px',
      background: state.isSelected ? '#CBD5E0' : '#FFFFFF',
    }),
    singleValue: (provided: any, { isDisabled }: any) => {
      const opacity = isDisabled ? 0.7 : 1;
      const transition = 'opacity 300ms';
      return { ...provided, opacity, transition };
    },
  };

  const handleChange = (option: any) => {
    setSelectedValue(option);
    if (onInstitutionChange) {
      onInstitutionChange(option?.value || null);
    }
  };

  if (loading) {
    return (
      <Box mb={4} role="status" aria-label="Carregando instituições" aria-busy="true">
        <Skeleton height="20px" width="190px" mb={2} />
        <Skeleton height="40px" maxWidth="350px" />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box mb={4} maxWidth="520px">
        <ErrorState title="Instituições indisponíveis" onRetry={fetchInstitutions} />
      </Box>
    );
  }

  if (institutions.length === 0) {
    return (
      <Box mb={4} maxWidth="520px">
        <EmptyState
          title="Nenhuma instituição encontrada"
          description="Não existem instituições disponíveis para seleção."
        />
      </Box>
    );
  }

  if (institutions.length === 1) return null;

  return (
    <Box mb={4}>
      <Text fontWeight="bold" mb={2}>
        Selecione a Instituição:
      </Text>
      <Select
        options={options}
        placeholder="Todas as instituições"
        noOptionsMessage={() => 'Nenhuma instituição encontrada'}
        onChange={handleChange}
        value={selectedValue}
        isClearable
        isSearchable
        styles={customStyles}
      />
    </Box>
  );
};
